import { h, Host } from "@stencil/core";
import { clamp, isValidNumber, isValidNumericKeyboardEvent, parseStringToStringNumber, sanitizeNumericInput, toMaxPrecision } from "../../utils/number-utils/numbers";
import { Direction } from "./interface";
export class InputFieldNumber {
    constructor() {
        this.inputValue = undefined;
        this.label = "";
        this.name = "";
        this.placeholder = "";
        this.defaultValue = undefined;
        this.disabled = false;
        this.required = false;
        this.error = false;
        this.errorMsg = "";
        this.hintText = "";
        this.unit = "";
        this.locale = undefined;
        this.min = undefined;
        this.max = undefined;
        this.precision = 0;
        this.step = 1;
        this.value = undefined;
        this.formatter = undefined;
        this.parser = undefined;
    }
    isTyping = false;
    inputFieldElement;
    /** Emitted when the value changes. */
    uxChange;
    /** Emitted when the input is invalid. */
    uxInvalid;
    updateInputValue() {
        let formatted = null;
        if (typeof this.formatter === "function") {
            formatted = this.formatter(this.value, this.isTyping);
        }
        if (formatted == null) {
            formatted = this.defaultFormatter();
        }
        this.inputValue = formatted;
    }
    /** Returns the native input element */
    async getInputElement() {
        return this.inputFieldElement.getInputElement();
    }
    /** Set focus on the native input element */
    setFocus() {
        return this.inputFieldElement.setFocus();
    }
    /** Validate the input value and set `error` accordingly. */
    async validate() {
        this.error =
            !isValidNumber(this.value) ||
                (this.min != null && this.min > this.value) ||
                (this.max != null && this.max < this.value);
        if (this.error) {
            this.uxInvalid.emit();
        }
    }
    connectedCallback() {
        // @ts-expect-error Check for typing
        this.value = isValidNumber(this.value) ? this.value : this.defaultValue;
        this.updateInputValue();
    }
    defaultFormatter() {
        if (isValidNumber(this.value)) {
            return this.value.toLocaleString(this.locale, {
                maximumFractionDigits: this.isTyping ? undefined : this.precision
            });
        }
        else {
            return this.inputValue;
        }
    }
    defaultParser() {
        const numString = parseStringToStringNumber(sanitizeNumericInput(this.inputValue, this.locale), this.locale);
        return numString === "" ? Number.NaN : Number(numString);
    }
    handleNextValue(value, isTyping = false) {
        this.isTyping = isTyping;
        if (this.value !== value) {
            this.value = value;
            this.uxChange.emit();
        }
    }
    getStepDelta(direction) {
        return direction * this.step;
    }
    incrementValue(delta) {
        const currentValue = isValidNumber(this.value) ? this.value : isValidNumber(this.defaultValue) ? this.defaultValue : 0;
        // @ts-expect-error Check for typing
        let nextValue = toMaxPrecision(currentValue + delta, this.precision);
        nextValue = clamp(nextValue, this.min, this.max);
        this.handleNextValue(nextValue);
    }
    handleKeydown = (event) => {
        const { key } = event;
        if (key === "ArrowUp") {
            this.incrementValue(this.getStepDelta(Direction.UP));
        }
        else if (key === "ArrowDown") {
            this.incrementValue(this.getStepDelta(Direction.DOWN));
        }
        else if (key === "Home" && this.min != null) {
            this.handleNextValue(this.min);
        }
        else if (key === "End" && this.max != null) {
            this.handleNextValue(this.max);
        }
        else if (key === "Enter") {
            this.handleBlur();
        }
    };
    handleKeypress = (event) => {
        if (!isValidNumericKeyboardEvent(event, this.locale)) {
            event.preventDefault();
        }
    };
    handlePaste = () => {
        return;
    };
    handleInput = () => {
        this.inputValue = this.inputFieldElement.value;
        const num = typeof this.parser === "function" ? this.parser(this.inputValue) : this.defaultParser();
        // @ts-expect-error Check for typing
        this.handleNextValue(num, true);
    };
    handleChange = (e) => {
        e.stopPropagation();
    };
    handleBlur = () => {
        let nextValue = this.value;
        if (isValidNumber(nextValue)) {
            nextValue = toMaxPrecision(this.value, this.precision);
        }
        else {
            // @ts-expect-error Check for typing
            nextValue = isValidNumber(this.defaultValue) ? this.defaultValue : 0;
        }
        if (isValidNumber(this.min) || isValidNumber(this.max)) {
            nextValue = clamp(nextValue, this.min, this.max);
        }
        this.handleNextValue(nextValue);
    };
    render() {
        const { handleKeydown, handleKeypress, handleChange, handleInput, handlePaste, handleBlur } = this;
        return (h(Host, { key: '19ae28ac1c07138c40957f56141d0705fb171a8b' }, h("ux-input-field", { key: '30753abb09b53df940cede8b5e68fe9fb21c80e7',
            // @ts-expect-error Check for typing
            ref: (el) => (this.inputFieldElement = el), type: "text", inputmode: "decimal", disabled: this.disabled, label: this.label, hintText: this.hintText, error: this.error, errorMsg: this.errorMsg, placeholder: this.placeholder, unit: this.unit, value: this.inputValue, required: this.required, onKeyDown: handleKeydown, onKeyPress: handleKeypress, onPaste: handlePaste, onUxChange: handleChange, onUxInput: handleInput, onUxBlur: handleBlur }, h("div", { key: '719c732d0bba8cf59c052acee96868eb2c3cd1f0', class: "ux-input-field-number__buttons", slot: "buttons" }, h("slot", { key: 'c304abc01a56ac114180f80c2196f9d742518e8e', name: "buttons" }))), this.name && h("input", { key: '5236fcefc95d495b49478da72753cdad0d353727', type: "hidden", name: this.name, value: this.value })));
    }
    static get is() { return "ux-input-field-number"; }
    static get originalStyleUrls() {
        return {
            "$": ["input-field-number.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["input-field-number.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label text"
                },
                "attribute": "label",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The name attribute of the input element"
                },
                "attribute": "name",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "placeholder": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Placeholder for the input element"
                },
                "attribute": "placeholder",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "defaultValue": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The default value of the input"
                },
                "attribute": "default-value",
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the input is disabled"
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "required": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the input is required"
                },
                "attribute": "required",
                "reflect": false,
                "defaultValue": "false"
            },
            "error": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the input value is invalid"
                },
                "attribute": "error",
                "reflect": false,
                "defaultValue": "false"
            },
            "errorMsg": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Optional error message that is rendered if `error` is set to `true`"
                },
                "attribute": "error-msg",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "hintText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Additional hint text shown below the input"
                },
                "attribute": "hint-text",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "An optional unit is shown besides the input"
                },
                "attribute": "unit",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "locale": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The locale to use for input validation."
                },
                "attribute": "locale",
                "reflect": false
            },
            "min": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The minimum value."
                },
                "attribute": "min",
                "reflect": false
            },
            "max": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The maximum value."
                },
                "attribute": "max",
                "reflect": false
            },
            "precision": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The maximum number of decimal places."
                },
                "attribute": "precision",
                "reflect": false,
                "defaultValue": "0"
            },
            "step": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The increment size to use when using keyboard navigation."
                },
                "attribute": "step",
                "reflect": false,
                "defaultValue": "1"
            },
            "value": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A valid number or `Number.NaN`."
                },
                "attribute": "value",
                "reflect": false
            },
            "formatter": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "FormatFn",
                    "resolved": "(value: string | number, isTyping: boolean) => string | null",
                    "references": {
                        "FormatFn": {
                            "location": "import",
                            "path": "./interface",
                            "id": "src/components/input-field-number/interface.ts::FormatFn"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A function that returns a string representation of a given number."
                }
            },
            "parser": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "ParseFn",
                    "resolved": "(value: string) => number | undefined",
                    "references": {
                        "ParseFn": {
                            "location": "import",
                            "path": "./interface",
                            "id": "src/components/input-field-number/interface.ts::ParseFn"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A function that parses a number from a given string."
                }
            }
        };
    }
    static get states() {
        return {
            "inputValue": {}
        };
    }
    static get events() {
        return [{
                "method": "uxChange",
                "name": "uxChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the value changes."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "uxInvalid",
                "name": "uxInvalid",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Emitted when the input is invalid."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "getInputElement": {
                "complexType": {
                    "signature": "() => Promise<HTMLInputElement>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLInputElement": {
                            "location": "global",
                            "id": "global::HTMLInputElement"
                        }
                    },
                    "return": "Promise<HTMLInputElement>"
                },
                "docs": {
                    "text": "Returns the native input element",
                    "tags": []
                }
            },
            "setFocus": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Set focus on the native input element",
                    "tags": []
                }
            },
            "validate": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Validate the input value and set `error` accordingly.",
                    "tags": []
                }
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "value",
                "methodName": "updateInputValue"
            }];
    }
}
//# sourceMappingURL=input-field-number.js.map
